import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { 
  Sparkles, 
  RefreshCw, 
  Lightbulb, 
  CheckCircle2, 
  TrendingUp, 
  Clock,
  AlertCircle
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface SpaceInsight {
  id: string;
  spaceId: string;
  userId: string;
  summary: string;
  keyTakeaways: string[];
  actionItems: string[];
  insights: string[];
  metadata: {
    model?: string;
    tokensUsed?: number;
    contextItems?: {
      topics: number;
      documents: number;
      sources: number;
      threads: number;
    };
  };
  status: string;
  generatedAt: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SpaceInsightsProps {
  spaceId: string;
  className?: string;
}

export function SpaceInsights({ spaceId, className = "" }: SpaceInsightsProps) {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);

  const { data: insight, isLoading, error } = useQuery<SpaceInsight | null>({
    queryKey: ["/api/spaces", spaceId, "insights"],
    queryFn: async () => {
      const res = await fetch(`/api/spaces/${spaceId}/insights`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch insights");
      return res.json();
    },
    enabled: !!spaceId,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/spaces/${spaceId}/insights`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to generate briefing");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/spaces", spaceId, "insights"] });
      queryClient.invalidateQueries({ queryKey: ["/api/credits"] });
      toast({
        title: "Briefing generated",
        description: "Your space briefing has been created.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to generate briefing",
        description: error.message || "Could not generate insights. Please try again.",
        variant: "destructive",
      });
    },
  });

  const isStale = insight?.expiresAt && new Date(insight.expiresAt) < new Date();

  if (isLoading) {
    return (
      <Card className={className} data-testid="card-space-insights-loading">
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className} data-testid="card-space-insights-error">
        <CardContent className="py-6 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Failed to load insights</p>
        </CardContent>
      </Card>
    );
  }

  if (!insight) {
    return (
      <Card className={className} data-testid="card-space-insights-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            AI Briefing
          </CardTitle>
          <CardDescription className="text-xs">
            Get AI-powered insights about your space
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="w-full"
            size="sm"
            data-testid="button-generate-insights"
          >
            {generateMutation.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Briefing
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Uses ~5 credits
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="card-space-insights">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            AI Briefing
            {isStale && (
              <Badge variant="secondary" className="text-xs">
                Outdated
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            title="Regenerate briefing"
            data-testid="button-regenerate-insights"
          >
            <RefreshCw className={`w-4 h-4 ${generateMutation.isPending ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>
            {formatDistanceToNow(new Date(insight.generatedAt), { addSuffix: true })}
          </span>
          {insight.metadata?.contextItems && (
            <>
              <span>•</span>
              <span>
                {insight.metadata.contextItems.topics} topics, {insight.metadata.contextItems.documents} docs
              </span>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <p className="text-sm text-foreground leading-relaxed" data-testid="text-insights-summary">
            {insight.summary}
          </p>
        </div>

        {insight.keyTakeaways && insight.keyTakeaways.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Lightbulb className="w-3 h-3" />
              Key Takeaways
            </div>
            <ul className="space-y-1" data-testid="list-key-takeaways">
              {insight.keyTakeaways.map((takeaway, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {insight.actionItems && insight.actionItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <CheckCircle2 className="w-3 h-3" />
              Suggested Actions
            </div>
            <ul className="space-y-1" data-testid="list-action-items">
              {insight.actionItems.map((action, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-3 h-3 mt-1 text-green-500 flex-shrink-0" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {insight.insights && insight.insights.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <TrendingUp className="w-3 h-3" />
              Patterns & Connections
            </div>
            <ul className="space-y-1" data-testid="list-patterns">
              {insight.insights.map((pattern, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <TrendingUp className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                  <span>{pattern}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
